import React from "react";
import {
  compose,
  withHandlers,
  withStateHandlers,
  withPropsOnChange,
} from "recompose";
import {
  Page,
  TableContainer,
  withTable,
  Pagination,
  TableSearch,
  ViewingRecords,
  AddNewButton,
} from "components/common";
import { XeroTableDataFetch } from "components";
import { Link } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  Table,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Button,
} from "reactstrap";
import PendingButton from "components/common/PendingButton";
import { get, isEmpty, keys, compact, filter, includes } from "lodash";
import qs from "query-string";
import BulkArchiveButton from "./ArchiveButton";
import { Trans } from "react-i18next";

import { withSelectedOrganisation } from "../../components/withSelectedOrganisation";
import { withContactGroups } from "../../components/withContactGroups";
import { formatAccountCurrency, exportDownload } from "../../services/util";

export const ContactsList = ({
  selected,
  setSelected,
  ordering,
  setOrdering,
  data,
  url,
  setData,
  limit,
  offset,
  setOffset,
  refreshToken,
  xeroFilters,
  match,
  search,
  setSearch,
  contact_group,
  contactGroups,
  addSelectedContactsToGroup,
  removeSelectedContactsFromGroup,
  removePending,
  isDeleteModalOpen,
  toggleDeleteModal,
  onArchived,
  error,
  setError,
  title,
  onExport,
}) => (
  <Page
    title={contact_group ? contact_group.Name : title || "Contacts"}
    translate={!contact_group}
  >
    <TableSearch onChange={setSearch} value={search} />
    <div className="mb-2 mt-3">
      <AddNewButton resource="Contact" match={{path: '/contacts', url: '/contacts'}}/>
      {!isEmpty(selected) && !contact_group && contactGroups ? (
        <UncontrolledButtonDropdown>
          <DropdownToggle caret className="ml-2" color="primary">
            <Trans>Add to Group</Trans>
          </DropdownToggle>
          <DropdownMenu>
            {contactGroups.map((cg) => (
              <DropdownItem
                key={cg.ContactGroupID}
                onClick={() => addSelectedContactsToGroup(cg)}
              >
                {cg.Name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledButtonDropdown>
      ) : (
        <Button color="primary" disabled className="mx-0 dropdown-toggle"><Trans>Add to Group</Trans></Button>
      )}
      {!isEmpty(selected) && contact_group && (
        <PendingButton
          className="ml-2"
          color="primary"
          onClick={() => removeSelectedContactsFromGroup(contact_group)}
          pending={removePending}
        >
          <Trans>Remove from Group</Trans>
        </PendingButton>
      )}
      {!isEmpty(selected) ? (
        <BulkArchiveButton
          selected={selected}
          buttonProps={{ className: "ml-2" }}
          style={{ display: "inline-block" }}
          toggleModal={toggleDeleteModal}
          isModalOpen={isDeleteModalOpen}
          onDeleted={onArchived}
          setError={setError}
        />
        ) : (
          <Button color="danger" className="ml-2" disabled><span className="fa fa-archive" /> <Trans>Archive Contacts</Trans></Button>
      )}
      <Button size="md" color="primary" onClick={onExport}>
        <Trans>Export</Trans> <span className="mx-1" /><i className="fas fa-file-excel" />
      </Button>
      {error && (
        <Alert color="danger">
          <Trans>{error.detail}</Trans>
        </Alert>
      )}
    </div>
    <div className="small inv-table">
      <TableContainer
        items={data.results}
        ordering={ordering}
        idKey="ContactID"
        onOrderingChanged={setOrdering}
        selected={selected}
        onSelectedChanged={setSelected}
      >
        <Table size="sm" className="small" responsive>
          <thead>
            <tr>
              <th select />
              <th sortBy="Name" className="text-left">
                <Trans>Contact</Trans>
              </th>
              <th sortBy="EmailAddress" className="text-nowrap">
                <Trans>Email</Trans>
              </th>
              <th className="text-right">
                <Trans>You Owe Them</Trans>
              </th>
              <th className="text-right">
                <Trans>They Owe You</Trans>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.loading && (
              <tr>
                <td colSpan="9" className="text-center">
                  <span className="fa fa-spinner fa-spin mt-2" />{" "}
                  <Trans>Loading...</Trans>
                </td>
              </tr>
            )}
            {data.results.map((i, idx) => {
              const payableOutstanding = get(
                i,
                "Balances.AccountsPayable.Outstanding",
                0
              );
              const payableOverdue = get(
                i,
                "Balances.AccountsPayable.Overdue",
                0
              );
              const receivableOutstanding = get(
                i,
                "Balances.AccountsReceivable.Outstanding",
                0
              );
              const receivableOverdue = get(
                i,
                "Balances.AccountsReceivable.Overdue",
                0
              );

              return (
                <tr key={i.ContactID}>
                  <td select />
                  <td className="text-left">
                    <Link to={`/contacts/view/${i.ContactID}`}>{i.Name}</Link>
                  </td>
                  <td>{i.EmailAddress || ("-")}</td>
                  <td className="text-right">
                    {payableOutstanding ? (
                      <>
                        <span className="d-block">
                          {formatAccountCurrency(payableOutstanding)}
                        </span>
                        {payableOverdue ? (
                          <span className="text-danger d-block">
                            {formatAccountCurrency(payableOverdue)}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      "-----"
                    )}
                  </td>
                  <td className="text-right">
                    {receivableOutstanding ? (
                      <>
                        <span className="d-block">
                          {formatAccountCurrency(receivableOutstanding)}
                        </span>
                        {receivableOverdue ? (
                          <span className="text-danger d-block">
                            {formatAccountCurrency(receivableOverdue)}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      "-----"
                    )}
                  </td>
                  <td className="text-end">
                    <Link to={`contacts/${i.ContactID}/statement`}>
                      <Trans>Statement</Trans>
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </div>
    {/* Ensure we have filters (even if they're empty) before fetching data, otherwise the unfiltered data will appear first when navigating to /contacts?contactGroup=23542-341-53-2345 */}
    {xeroFilters && (
      <XeroTableDataFetch
        ordering={ordering}
        refreshToken={refreshToken}
        filters={xeroFilters}
        limit={limit}
        url={url}
        offset={!contact_group ? offset : 0}
        onDataFetched={setData}
      />
    )}



<div className="clearfix">
   <div className="float-right">
      <Pagination
        size="sm"
        noPages
        limit={limit}
        offset={offset}
        onOffsetChanged={setOffset}
      />
    </div>
    <ViewingRecords
      offset={offset}
      limit={limit}
      noCount
      className="float-left"
    />

   </div>

    
  </Page>
);

export const enhance = compose(
  withStateHandlers(
    {
      url: "contacts",
    },
    {
      setState: () => (state) => ({ ...state }),
      setError: () => (error) => ({ error }),
    }
  ),
  withPropsOnChange(
    (props, next) => props.location.search !== next.location.search,
    ({ location, xero, setState }) => {
      const q = qs.parse(location.search);

      if (q.contactGroup) {
        Promise.props({
          contact_group: xero
            .get(`contactgroups/${q.contactGroup}`)
            .get("data"),
        }).then(({ contact_group }) => {
          setState({
            // If the group has no contacts, use the ContactGroupID as an ID that won't return any values (otherwise we get all contacts without filtering)
            contact_ids:
              contact_group.Contacts.length > 0
                ? contact_group.Contacts.map((c) => c.ContactID)
                : [contact_group.ContactGroupID],
            contact_group,
          });
        });
      } else {
        setState({ contact_ids: null, contact_group: null });
      }
    }
  ),
  withTable({
    limit: 100,
  }),
  withPropsOnChange(
    ["search", "contact_ids", "location", "offset"],
    ({ search, contact_ids, location, setState, offset, setLimit }) => {
      let { isCustomer, isSupplier, contactGroup } = qs.parse(location.search);
      let limit = 100;
      if (contactGroup) {
        limit = 20;
      }
      setLimit(limit);
      isCustomer = isCustomer === "true";
      isSupplier = isSupplier === "true";

      const input = (search || "").replace(/"/g, "").toLowerCase();
      let params = {};
      const raw = [
        isSupplier ? `IsSupplier=true` : "",
        isCustomer ? `IsCustomer=true` : "",
      ];
      if (isSupplier) {
        params.IsSupplier = true;
      }
      if (isCustomer) {
        params.IsCustomer = true;
      }

      if (!!input) {
        raw.push(
          `(Name.ToLower().Contains("${input}") OR (EmailAddress != null AND EmailAddress.ToLower().Contains("${input}")))`
        );
        params.search = input;
      }
      if (!!contact_ids) {
        let ids = contact_ids.slice(offset, limit + offset);
        if (ids.length === 0) {
          ids = [contactGroup];
        }
        params["extra_params"] = {
          IDs: contact_ids ? ids.join(",") : null,
        };
        params.contact_ids = contact_ids ? ids.join(",") : "";
      } else if (contactGroup) {
        // Don't set the filters if there is a contactgroup but we havn't got the contact IDs yet
        return;
      }

      params.raw = compact(raw).join(" AND ");

      setState({ xeroFilters: params });

      return {
        title: isCustomer ? "Customers" : isSupplier ? "Suppliers" : "Contacts",
      };
    }
  ),
  withSelectedOrganisation(),
  withContactGroups(),
  withHandlers({
    addSelectedContactsToGroup:
      ({ client, selected, selectedOrganisation, t }) =>
      (contactGroup) => {
        client
          .post(
            `xero_actions/${selectedOrganisation.id}/add_contacts_to_group`, {'Contacts':keys(selected).map((x) => ({ ContactID: x })), ContactGroup:contactGroup.ContactGroupID}
          )
          .then((resp) =>
            NotificationManager.success(
              t("Added {{count}}  contacts to {{contactGroup.Name}}", {
                count: keys(selected).length,
                contactGroup,
              })
            )
          );
      },
    removeSelectedContactsFromGroup:
      ({
        xero,
        selected,
        selectedOrganisation,
        setState,
        xeroFilters,
        t,
        contact_ids,
      }) =>
      (contactGroup) => {
        setState({ removePending: true });
        let urls = keys(selected).map(
          (x) => `contactgroups/${contactGroup.ContactGroupID}/contacts/${x}`
        );
        Promise.all(urls.map((x) => xero.delete(x)))
          .then((resp) => {
            NotificationManager.success(
              t("Removed {{ count }} contacts from {{ contactGroup.Name }}", {
                count: keys(selected).length,
                contactGroup,
              })
            );
            let newContactIds = filter(
              [...contact_ids],
              (x) => !includes(keys(selected), x)
            );
            if (newContactIds.length === 0) {
              newContactIds = [contactGroup.ContactGroupID];
            }
            setState({ contact_ids: newContactIds }); // Trigger a reload
          })
          .finally(() => {
            setState({ removePending: false });
          });
      },
    onArchived:
      ({ setState, xeroFilters, t }) =>
      (failed) => {
        NotificationManager.success(t("Archive successful"));
        setState({ xeroFilters: { ...xeroFilters } }); // Trigger a reload
      },
    onExport: ({ client, xeroFilters, selectedOrganisation, t }) => () => { 
      NotificationManager.success(t("Exporting..."));

      let params = Object.assign({}, xeroFilters);
      params['export'] = true;
      let qs = JSON.stringify(params);

      exportDownload(client, `xero/${selectedOrganisation.id}/contacts?query=${qs}`, 'Contacts.xlsx');
    }
  })
);

export default enhance(ContactsList);
